import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../services/api/client.js';
import {
  attemptAutomaticConsent,
  bannerDismissalStorageKey,
  billingIsReal,
  consentAttemptStorageKey,
  pendingOAuthSnapshot,
  validatedPendingConsent,
} from '../lib/pendingOAuth.js';
import { useAuth } from './AuthContext.jsx';
import { attemptAutomaticPersonalPublishing, continuePersonalPublishing, personalPublishingConsent } from '../lib/personalPublishing.js';

const PendingOAuthContext = createContext(null);

function readSessionFlag(key) {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeSessionFlag(key) {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch { /* the choice simply lasts until this page is reloaded */ }
}

function availableAutomaticStorage() {
  try {
    // Shared across same-origin tabs so only one can consume the consent CSRF
    // cookie. pendingOAuth.js stores only a SHA-256 key and the value "1".
    return window.localStorage;
  } catch {
    return null;
  }
}

function pageIsVisible() {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function hasStayIntent(search) {
  try {
    return new URLSearchParams(search).get('mcp_stay') === '1';
  } catch {
    return false;
  }
}

export function PendingOAuthProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const userId = user?.id;
  const stayIntentPresent = hasStayIntent(location.search);
  const [pending, setPendingState] = useState(null);
  const [personalPendingState, setPersonalPendingState] = useState({ userId: null, choices: {} });
  const [personalProgress, setPersonalProgress] = useState({ userId: null, status: 'idle' });
  const personalPendingRef = useRef(personalPendingState);
  personalPendingRef.current = personalPendingState;
  const personalRequestRef = useRef(null);
  const [billingReal, setBillingReal] = useState(false);
  const [bannerDismissal, setBannerDismissal] = useState({ userId: null, resumeUrl: null, dismissed: false });
  const [consentNavigationStatus, setConsentNavigationStatus] = useState('idle');
  const [pageVisible, setPageVisible] = useState(pageIsVisible);
  const [checkoutChoiceDeferred, setCheckoutChoiceDeferred] = useState(() => {
    try {
      return location.pathname === '/app/settings'
        && new URLSearchParams(location.search).get('checkout') === 'success';
    } catch {
      return false;
    }
  });
  const currentRef = useRef({ userId, pending, billingReal, checkoutChoiceDeferred, pageVisible, stayIntentPresent });
  currentRef.current = { userId, pending, billingReal, checkoutChoiceDeferred, pageVisible, stayIntentPresent };
  const mountedRef = useRef(false);
  const pendingRevalidationRef = useRef(null);
  const setPending = useCallback((next) => {
    if (typeof next !== 'function') currentRef.current.pending = next;
    setPendingState((current) => {
      const value = typeof next === 'function' ? next(current) : next;
      currentRef.current.pending = value;
      return value;
    });
    if (next == null) setConsentNavigationStatus('idle');
  }, []);

  // AuthenticatedRoutes survives a same-tab account switch. Immediately make
  // old pending/billing state ineligible (pending.userId is also checked
  // synchronously), then reset the user-scoped presentation state.
  useEffect(() => {
    setPending(null);
    setBillingReal(false);
    setBannerDismissal({ userId: null, resumeUrl: null, dismissed: false });
    setConsentNavigationStatus('idle');
    setPersonalPendingState({ userId, choices: {} });
    setPersonalProgress({ userId, status: 'idle' });
  }, [userId]);

  const setPersonalPending = useCallback((connector, candidate, expectedUserId) => {
    if (currentRef.current.userId !== expectedUserId || !['chatgpt', 'claude'].includes(connector)) return;
    // Keep the other host's valid pending request even without Free capability:
    // it still makes an automatic choice ambiguous. Missing keys mean discovery
    // has not completed, rather than proving that no other host is pending.
    const value = candidate?.connector === connector && validatedPendingConsent(candidate, { userId: expectedUserId })
      ? candidate : null;
    setPersonalPendingState(current => ({ userId: expectedUserId,
      choices: { ...(current.userId === expectedUserId ? current.choices : {}), [connector]: value } }));
  }, []);

  const canAutomaticallyContinuePersonal = useCallback((candidate) => {
    const current = currentRef.current;
    const choices = personalPendingRef.current;
    return !current.billingReal
      && !current.stayIntentPresent && !current.checkoutChoiceDeferred
      && current.pageVisible && pageIsVisible()
      && current.pending?.automatic !== false
      && current.pending?.userId === current.userId
      && current.pending?.resumeUrl === candidate?.resumeUrl
      && choices.userId === current.userId
      && Object.prototype.hasOwnProperty.call(choices.choices, 'claude')
      && !choices.choices.claude
      && candidate?.connector === 'chatgpt'
      && Boolean(personalPublishingConsent(candidate, current.userId));
  }, []);

  const continuePersonal = useCallback(async (connector, automatic = false) => {
    const candidate = personalPendingRef.current.userId === userId ? personalPendingRef.current.choices[connector] : null;
    if (!personalPublishingConsent(candidate, userId)
      || personalRequestRef.current) return;
    if (automatic && !canAutomaticallyContinuePersonal(candidate)) return;
    const request = { userId, candidate };
    personalRequestRef.current = request;
    setPersonalProgress({ userId, status: 'saving', automatic });
    const isCurrent = () => mountedRef.current && currentRef.current.userId === userId
      && personalPendingRef.current.userId === userId && personalPendingRef.current.choices[connector] === candidate
      && (!automatic || canAutomaticallyContinuePersonal(candidate));
    try {
      const storage = availableAutomaticStorage();
      let manualMarkerKey = null;
      if (!automatic && storage) {
        try { manualMarkerKey = await consentAttemptStorageKey(candidate, userId); } catch { /* explicit recovery remains usable */ }
      }
      const options = { pending: candidate, userId, apiClient: api,
        navigate: url => {
          if (manualMarkerKey) {
            try { storage.setItem(manualMarkerKey, '1'); } catch { /* explicit recovery remains usable */ }
          }
          window.location.assign(url);
        }, isCurrent };
      const result = automatic
        ? await attemptAutomaticPersonalPublishing({ ...options, storage,
          locks: globalThis.navigator?.locks, visible: pageVisible })
        : await continuePersonalPublishing(options);
      if (!mountedRef.current || currentRef.current.userId !== userId) return;
      setPersonalProgress({ userId, automatic, status: result.status === 'navigated' ? 'returning'
        : result.status === 'changed' ? 'changed' : result.status === 'failed' ? 'failed' : 'manual' });
      if (result.status === 'changed') setPersonalPending(connector, result.pending, userId);
    } catch {
      if (isCurrent()) setPersonalProgress({ userId, status: 'failed' });
    } finally {
      if (personalRequestRef.current === request) personalRequestRef.current = null;
    }
  }, [userId, setPersonalPending, canAutomaticallyContinuePersonal, pageVisible]);

  useEffect(() => {
    if (personalProgress.userId !== userId || personalProgress.status !== 'idle'
      || personalPendingState.userId !== userId) return;
    const candidate = personalPendingState.choices.chatgpt;
    if (canAutomaticallyContinuePersonal(candidate)) continuePersonal('chatgpt', true);
  }, [personalPendingState, personalProgress, pending, userId, billingReal,
    checkoutChoiceDeferred, pageVisible, stayIntentPresent, continuePersonal, canAutomaticallyContinuePersonal]);

  useEffect(() => {
    let cancelled = false;
    const candidate = pending;
    const identity = { userId, resumeUrl: candidate?.resumeUrl || null };
    setBannerDismissal((current) => (
      current.userId === identity.userId && current.resumeUrl === identity.resumeUrl
        ? current
        : { ...identity, dismissed: false }
    ));
    if (!candidate || candidate.userId !== userId) return () => { cancelled = true; };
    bannerDismissalStorageKey(candidate, userId).then((key) => {
      if (cancelled || !mountedRef.current
        || currentRef.current.userId !== userId || currentRef.current.pending !== candidate || !key) return;
      const storedDismissal = readSessionFlag(key);
      setBannerDismissal((current) => (
        current.userId === identity.userId && current.resumeUrl === identity.resumeUrl
          ? { ...identity, dismissed: current.dismissed || storedDismissal }
          : current
      ));
    }).catch(() => { /* hashing unavailable leaves this notice visible */ });
    return () => { cancelled = true; };
  }, [pending, userId]);

  const updateEntitlements = useCallback((entitlements) => {
    // A late stale response must not undo a real-billing answer observed by
    // handlePaid/PreviewProvider during the same short-lived pending consent.
    if (currentRef.current.userId !== userId) return;
    setBillingReal((current) => current || billingIsReal(entitlements));
  }, [userId]);

  // /onboarding has no billing gate or PreviewProvider. Once (and only once)
  // a real pending consent is known, read the cached/deduplicated entitlement
  // endpoint so that route can make the same decision as /app.
  useEffect(() => {
    if (!pending || pending.userId !== userId) return;
    let cancelled = false;
    api.billing.getEntitlements()
      .then((entitlements) => { if (!cancelled) updateEntitlements(entitlements); })
      .catch(() => { /* unproven billing stays in the waiting state */ });
    return () => { cancelled = true; };
  }, [pending, updateEntitlements, userId]);

  // If an earlier gate redirects away before Settings can mount its welcome,
  // there is no overlay to wait for. Do not strand the return choice forever.
  useEffect(() => {
    if (checkoutChoiceDeferred && location.pathname !== '/app/settings') {
      setCheckoutChoiceDeferred(false);
    }
  }, [checkoutChoiceDeferred, location.pathname]);

  useEffect(() => {
    if (!pending || pending.userId !== userId || pending.automatic === false
      || stayIntentPresent || !billingReal || checkoutChoiceDeferred || !pageVisible) return undefined;
    let cancelled = false;
    setConsentNavigationStatus('checking');
    attemptAutomaticConsent({
      pending,
      userId,
      billingReal,
      checkoutChoiceDeferred,
      storage: availableAutomaticStorage(),
      navigate: (url) => window.location.assign(url),
      getPendingOAuth: () => api.auth.getPendingOAuth(),
      locks: globalThis.navigator?.locks,
      visible: pageVisible,
      isCurrent: () => !cancelled
        && currentRef.current.userId === userId
        && currentRef.current.pending === pending
        && currentRef.current.billingReal
        && !currentRef.current.checkoutChoiceDeferred
        && currentRef.current.pageVisible
        && pageIsVisible(),
    }).then((result) => {
      if (cancelled || currentRef.current.userId !== userId || currentRef.current.pending !== pending) return;
      if (Object.prototype.hasOwnProperty.call(result, 'pending') && (
        result.pending == null
        || result.pending.resumeUrl !== pending.resumeUrl
        || result.pending.expiresAt !== pending.expiresAt
        || result.pending.automatic !== pending.automatic
      )) setPending(result.pending);
      setConsentNavigationStatus(
        result.status === 'navigated' ? 'attempted' : result.status === 'changed' ? 'manual' : result.status
      );
    });
    return () => { cancelled = true; };
  }, [pending, userId, billingReal, checkoutChoiceDeferred, pageVisible, stayIntentPresent]);

  const showManualPending = useCallback((candidate, expectedUserId) => {
    if (currentRef.current.userId !== expectedUserId || candidate?.userId !== expectedUserId) return false;
    const manualPending = { ...candidate, automatic: false };
    setPending(manualPending);
    setConsentNavigationStatus(
      validatedPendingConsent(manualPending, { userId: expectedUserId }) ? 'manual' : 'invalid'
    );
    return true;
  }, []);

  const revalidateKnownPending = useCallback(() => {
    if (!pageIsVisible() || pendingRevalidationRef.current) return pendingRevalidationRef.current;
    const original = currentRef.current.pending;
    const expectedUserId = currentRef.current.userId;
    if (!original || expectedUserId == null || original.userId !== expectedUserId) return null;
    // Visibility restoration re-runs an eligible automatic transition. Let
    // that path own its check; billing/checkout/Stay blockers still need the
    // passive read so a completed connection does not leave a stale banner.
    if (!currentRef.current.pageVisible && original.automatic !== false
      && currentRef.current.billingReal
      && !currentRef.current.checkoutChoiceDeferred
      && !currentRef.current.stayIntentPresent
      && validatedPendingConsent(original, { userId: expectedUserId })) return null;

    const request = (async () => {
      let payload;
      try {
        payload = await api.auth.getPendingOAuth();
      } catch {
        return;
      }
      if (!mountedRef.current || !pageIsVisible()
        || currentRef.current.userId !== expectedUserId
        || currentRef.current.pending !== original) return;

      if (payload && typeof payload === 'object' && payload.pending === false) {
        setPending(null);
        return;
      }
      if (!payload || typeof payload !== 'object' || payload.pending !== true) return;

      const latest = pendingOAuthSnapshot(payload, expectedUserId);
      if (!latest) {
        setPending(null);
        return;
      }
      const originalTarget = validatedPendingConsent(original, { userId: expectedUserId });
      const latestTarget = validatedPendingConsent(latest, { userId: expectedUserId });
      if (!latestTarget) {
        setPending({ ...latest, automatic: false });
        setConsentNavigationStatus('invalid');
      } else if (latestTarget !== originalTarget) {
        setPending({ ...latest, automatic: false });
        setConsentNavigationStatus('manual');
      } else if (latest.expiresAt !== original.expiresAt) {
        setPending({ ...latest, automatic: original.automatic });
      }
    })();
    pendingRevalidationRef.current = request;
    request.finally(() => {
      if (pendingRevalidationRef.current === request) pendingRevalidationRef.current = null;
    });
    return request;
  }, [setPending]);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return undefined;
    mountedRef.current = true;
    const onVisibilityChange = () => {
      const visible = pageIsVisible();
      setPageVisible(visible);
      if (visible) revalidateKnownPending();
    };
    const onPageActivation = () => { if (pageIsVisible()) revalidateKnownPending(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onPageActivation);
    window.addEventListener('pageshow', onPageActivation);
    return () => {
      mountedRef.current = false;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onPageActivation);
      window.removeEventListener('pageshow', onPageActivation);
    };
  }, [revalidateKnownPending]);

  // Manual recovery deliberately does not emit chatgpt_returned: this URL is
  // the MCP server's consent review, not a completed return to ChatGPT.
  const resume = useCallback(async () => {
    if (!billingReal || checkoutChoiceDeferred) return;
    const original = pending;
    setConsentNavigationStatus('checking');
    let payload;
    try {
      payload = await api.auth.getPendingOAuth();
    } catch {
      if (currentRef.current.userId === userId && currentRef.current.pending === original) {
        setConsentNavigationStatus('manual');
      }
      return;
    }
    if (currentRef.current.userId !== userId || currentRef.current.pending !== original) return;

    if (payload && typeof payload === 'object' && payload.pending === false) {
      setPending(null);
      return;
    }

    const latest = pendingOAuthSnapshot(payload, userId);
    if (!latest) {
      setPending(null);
      return;
    }
    const originalTarget = validatedPendingConsent(original, { userId });
    let latestTarget = validatedPendingConsent(latest, { userId });
    if (!originalTarget || !latestTarget || originalTarget !== latestTarget) {
      setPending({ ...latest, automatic: false });
      setConsentNavigationStatus(latestTarget ? 'manual' : 'invalid');
      return;
    }

    // Hashing may yield while the user or pending request changes. Derive the
    // existing opaque marker first, then re-check the lifecycle and expiry
    // before recording it immediately ahead of the browser hand-off.
    let markerKey = null;
    const storage = availableAutomaticStorage();
    if (storage) {
      try {
        markerKey = await consentAttemptStorageKey(latest, userId);
      } catch { /* storage/hash failure must not disable an explicit action */ }
    }
    if (currentRef.current.userId !== userId || currentRef.current.pending !== original) return;
    latestTarget = validatedPendingConsent(latest, { userId });
    if (!latestTarget || latestTarget !== originalTarget) {
      setPending({ ...latest, automatic: false });
      setConsentNavigationStatus('invalid');
      return;
    }
    if (markerKey) {
      try { storage.setItem(markerKey, '1'); } catch { /* manual navigation remains available */ }
    }
    setPending({ ...latest, automatic: false });
    try {
      window.location.assign(latestTarget);
      setConsentNavigationStatus('attempted');
    } catch {
      setConsentNavigationStatus('manual');
    }
  }, [pending, userId, billingReal, checkoutChoiceDeferred]);

  const dismissBanner = useCallback(async () => {
    const candidate = currentRef.current.pending;
    const expectedUserId = currentRef.current.userId;
    if (!candidate || candidate.userId !== expectedUserId) return;
    setBannerDismissal({ userId: expectedUserId, resumeUrl: candidate.resumeUrl || null, dismissed: true });
    let key = null;
    try { key = await bannerDismissalStorageKey(candidate, expectedUserId); } catch { /* in-memory dismissal still works */ }
    if (!mountedRef.current
      || currentRef.current.userId !== expectedUserId || currentRef.current.pending !== candidate) return;
    if (key) writeSessionFlag(key);
  }, []);

  const releaseCheckoutChoice = useCallback(() => setCheckoutChoiceDeferred(false), []);
  const manualConsentAvailable = Boolean(
    billingReal
    && !checkoutChoiceDeferred
    && validatedPendingConsent(pending, { userId })
    && (consentNavigationStatus === 'attempted' || consentNavigationStatus === 'manual')
  );
  const restartConsentRequired = Boolean(
    pending
    && billingReal
    && !checkoutChoiceDeferred
    && !validatedPendingConsent(pending, { userId })
  );
  const connectionTransitionActive = Boolean(
    billingReal && !checkoutChoiceDeferred && consentNavigationStatus === 'checking'
    || personalProgress.userId === userId && personalProgress.automatic
      && ['saving', 'returning'].includes(personalProgress.status)
  );
  const bannerDismissed = Boolean(
    pending
    && bannerDismissal.dismissed
    && bannerDismissal.userId === userId
    && bannerDismissal.resumeUrl === pending.resumeUrl
  );

  const value = {
    pending,
    setPending,
    showManualPending,
    billingReal,
    updateEntitlements,
    bannerDismissed,
    consentNavigationStatus,
    manualConsentAvailable,
    restartConsentRequired,
    connectionTransitionActive,
    resume,
    dismissBanner,
    releaseCheckoutChoice,
    setPersonalPending,
    personalPendingOptions: personalPendingState.userId === userId
      ? Object.values(personalPendingState.choices).filter(candidate => personalPublishingConsent(candidate, userId)) : [],
    personalStartStatus: personalProgress.userId === userId ? personalProgress.status : 'idle',
    personalAutomaticTransition: personalProgress.userId === userId && personalProgress.automatic
      && ['saving', 'returning'].includes(personalProgress.status),
    continuePersonal,
  };

  return (
    <PendingOAuthContext.Provider value={value}>
      {children}
    </PendingOAuthContext.Provider>
  );
}

export function usePendingOAuth() {
  const context = useContext(PendingOAuthContext);
  if (!context) throw new Error('usePendingOAuth must be used inside PendingOAuthProvider');
  return context;
}

export default PendingOAuthProvider;
